import { apiRequest } from '../client'
import type {
  CreateProductRequest,
  CreateProductResponse,
  Product,
  ProductListResponse,
  ProductLookupResponse,
  RestaurantVolumeTier,
  UpdateProductRequest,
  VolumeTier,
} from '../types'

interface ProductListParams {
  search?: string
  status?: 'all' | 'active' | 'inactive'
  page?: number
  pageSize?: number
  restaurantId?: number
  categoryCode?: string
  openPrice?: boolean
  /**
   * CHG-2026-09-duplicate-product-merge: drop merged duplicates from the result — for a picker
   * that must not offer one for selling (e.g. the menu planner's "add product" search). The plain
   * product list omits this, so a duplicate stays visible there with its badge.
   */
  excludeMerged?: boolean
  /**
   * CHG-2026-09-duplicate-product-merge: keep only webbill-imported products — the merge dialog's
   * primary picker, which may only target another webbill-imported product.
   */
  webbillOnly?: boolean
  /**
   * CHG-2026-09-stock-usage-and-price-twins: keep only products that can be a part of a dish —
   * no dishes, variants, merged duplicates or deleted products, filtered on the server so paging
   * and `total` are right. The parts search of the "Lagerverbrauch" tab.
   */
  stockArticlesOnly?: boolean
}

/** One change a merge makes on the primary's behalf, as listed by the merge preview. */
export type MergeChange = {
  restaurantId: number
  restaurantName?: string
} & (
  | {
      type: 'master_menu' | 'daily_menu'
      menuName: string
      menuDate?: string
      sectionName: string
      primaryAlreadyThere: boolean
    }
  | { type: 'kiosk_tile'; page: string; primaryAlreadyThere: boolean }
  | { type: 'checkout_addon'; primaryAlreadyThere: boolean }
  | { type: 'price_copied'; amountCents: number }
  | { type: 'assigned' }
  | { type: 'price_differs'; primaryCents: number; duplicateCents: number }
)

/**
 * Merge preview entries without a restaurant (CHG-2026-09-stock-usage-and-price-twins, D12): a
 * dish whose part is rewritten to the primary, and a variant that moves to the primary.
 */
export type MergeCatalogChange =
  | {
      type: 'stock_usage_part'
      dishSku: string
      dishName: string
      primaryAlreadyThere: boolean
    }
  | { type: 'variant'; variantSku: string; variantName: string }

export const productsApi = {
  /**
   * Browse a SKU-ordered page of the catalogue.
   *
   * Do not use this to resolve SKUs you already know — the page boundary drops the tail of the
   * catalogue and the missing rows are indistinguishable from products that do not exist. Use
   * {@link productsApi.lookup} for that.
   */
  list: (params?: ProductListParams) => {
    const searchParams = new URLSearchParams()
    if (params?.search) searchParams.set('search', params.search)
    if (params?.status) searchParams.set('status', params.status)
    if (params?.page !== undefined)
      searchParams.set('page', params.page.toString())
    if (params?.pageSize)
      searchParams.set('pageSize', params.pageSize.toString())
    if (params?.restaurantId)
      searchParams.set('restaurantId', params.restaurantId.toString())
    if (params?.categoryCode)
      searchParams.set('categoryCode', params.categoryCode)
    if (params?.openPrice !== undefined)
      searchParams.set('openPrice', String(params.openPrice))
    if (params?.excludeMerged)
      searchParams.set('excludeMerged', String(params.excludeMerged))
    if (params?.webbillOnly)
      searchParams.set('webbillOnly', String(params.webbillOnly))
    if (params?.stockArticlesOnly) searchParams.set('stockArticlesOnly', 'true')

    const query = searchParams.toString()
    return apiRequest<ProductListResponse>(
      `/api/v0/admin/products${query ? `?${query}` : ''}`,
    )
  },

  /**
   * Resolve an explicit set of SKUs, scoped to a restaurant for prices and assignment.
   *
   * Correct at any catalogue size: the result is bounded by the SKUs asked for, not by a page.
   *
   * `includeStock` adds `availableQty` per item. It needs a restaurant and the inventory-view
   * permission — ask for it only when the caller holds that permission, or the whole lookup is
   * refused and the caller loses price and assignment state along with the quantity.
   */
  lookup: (
    skus: Array<string>,
    restaurantId?: number,
    includeStock = false,
  ) => {
    const searchParams = new URLSearchParams()
    searchParams.set('skus', skus.join(','))
    if (restaurantId) searchParams.set('restaurantId', restaurantId.toString())
    if (includeStock && restaurantId) searchParams.set('includeStock', 'true')
    return apiRequest<ProductLookupResponse>(
      `/api/v0/admin/products/lookup?${searchParams.toString()}`,
    )
  },

  get: (sku: string) =>
    apiRequest<Product>(`/api/v0/admin/products/${encodeURIComponent(sku)}`),

  create: (data: CreateProductRequest) =>
    apiRequest<CreateProductResponse>('/api/v0/admin/products', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (sku: string, data: UpdateProductRequest) =>
    apiRequest<{ ok: boolean; updated: number }>(
      `/api/v0/admin/products/${encodeURIComponent(sku)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
    ),

  /**
   * Sets only whether the guest is asked warm or cold (CHG-2026-09-menu-editor-redesign).
   *
   * Deliberately not `update()`: a PATCH on the product rebuilds a catalogue bundle for every
   * restaurant of the tenant and fires an ESL fan-out, which is far too much for a toggle the
   * menu planner offers on every row.
   */
  setTemperatureChoice: (sku: string, temperatureChoice: boolean) =>
    apiRequest<{ ok: boolean }>(
      `/api/v0/admin/products/${encodeURIComponent(sku)}/temperature-choice`,
      {
        method: 'PUT',
        body: JSON.stringify({ temperatureChoice }),
      },
    ),

  /**
   * Soft-deletes a product. A source with live variants is refused with `409
   * product_has_variants` (listing `variants`) unless `withVariants` is set, which deletes source
   * and variants together; a part of a live dish is refused with `409 product_is_part` (listing
   * `dishes`) — CHG-2026-09-stock-usage-and-price-twins. Without the option the request is the
   * same as always.
   */
  delete: (sku: string, options?: { withVariants?: boolean }) =>
    apiRequest<{ ok: boolean; deleted: boolean }>(
      `/api/v0/admin/products/${encodeURIComponent(sku)}${
        options?.withVariants ? '?withVariants=true' : ''
      }`,
      {
        method: 'DELETE',
      },
    ),

  listVolumeTiers: (sku: string) =>
    apiRequest<{ items: Array<RestaurantVolumeTier> }>(
      `/api/v0/admin/products/${encodeURIComponent(sku)}/volume-tier`,
    ),

  setVolumeTier: (sku: string, restaurantId: number, tier: VolumeTier) =>
    apiRequest<{ ok: boolean }>(
      `/api/v0/admin/products/${encodeURIComponent(sku)}/volume-tier/${restaurantId}`,
      {
        method: 'PUT',
        body: JSON.stringify(tier),
      },
    ),

  deleteVolumeTier: (sku: string, restaurantId: number) =>
    apiRequest<{ ok: boolean }>(
      `/api/v0/admin/products/${encodeURIComponent(sku)}/volume-tier/${restaurantId}`,
      {
        method: 'DELETE',
      },
    ),

  /**
   * Dry run of {@link productsApi.merge}: the same 409 codes, plus the changes the merge would make.
   * Writes nothing.
   */
  mergePreview: (sku: string, primarySku: string) =>
    apiRequest<{ changes: Array<MergeChange | MergeCatalogChange> }>(
      `/api/v0/admin/products/${encodeURIComponent(sku)}/merge/preview`,
      { method: 'POST', body: JSON.stringify({ primarySku }) },
    ),

  /**
   * Merges a duplicate webbill article into its primary (CHG-2026-09-duplicate-product-merge);
   * the primary takes over the duplicate's menu items, kiosk tiles, add-ons and missing prices.
   * 409 error codes: `merge_self`, `merge_chain`, `merge_not_webbill`, `already_merged`,
   * `count_session_open`, `merge_primary_inactive`.
   */
  merge: (sku: string, primarySku: string) =>
    apiRequest<{
      ok: boolean
      product: Product
      primary: Product
      bundlesPublished: number
      changes: Array<MergeChange | MergeCatalogChange>
    }>(`/api/v0/admin/products/${encodeURIComponent(sku)}/merge`, {
      method: 'POST',
      body: JSON.stringify({ primarySku }),
    }),

  /** Undoes a merge: the duplicate becomes independently sellable/countable again. */
  unmerge: (sku: string) =>
    apiRequest<{ ok: boolean; product: Product; bundlesPublished: number }>(
      `/api/v0/admin/products/${encodeURIComponent(sku)}/merge`,
      { method: 'DELETE' },
    ),
}
